import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { KeyRound, Banknote, CheckCircle2 } from 'lucide-react';
import { api, fcfa } from '../lib/api';
import { Button } from './ui/button';

const FIELDS = [
  ['wave_api_key', 'Wave — clé API (wave_sn_prod_…)'],
  ['wave_webhook_secret', 'Wave — secret webhook (wave_sn_WHS_…)'],
  ['wave_api_signing_secret', 'Wave — secret de signature API (optionnel)'],
  ['om_client_id', 'Orange Money — client_id'],
  ['om_client_secret', 'Orange Money — client_secret'],
  ['om_merchant_key', 'Orange Money — merchant key'],
  ['sms_client_id', 'Orange SMS API — client_id'],
  ['sms_client_secret', 'Orange SMS API — client_secret'],
  ['sms_sender', 'Orange SMS API — numéro expéditeur (+221…)'],
];

export const PaymentSettings = () => {
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState({});
  const [payouts, setPayouts] = useState(null);

  const load = async () => {
    try {
      const [{ data }, { data: p }] = await Promise.all([api.get('/admin/settings/payments'), api.get('/admin/payouts')]);
      setCfg(data); setPayouts(p); setForm({ om_env: data.om_env || 'sn' });
    } catch {}
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try { await api.post('/admin/settings/payments', form); toast.success('Clés enregistrées'); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
  };
  const markPaid = async (id) => {
    try { await api.post(`/admin/payouts/${id}/paid`); toast.success('Reversement enregistré'); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
  };

  if (!cfg) return null;
  return (
    <>
      <section className="card-elev rounded-2xl p-5" data-testid="payment-settings">
        <h2 className="font-display text-xl font-bold flex items-center gap-2"><KeyRound className="h-5 w-5 text-sky-400" />Paiements via la plateforme (Wave & Orange Money)</h2>
        <p className="text-sm text-slate-400 mt-1">Collez vos clés marchandes : l'acheteuse paiera la plateforme, votre commission sera prélevée automatiquement et vous reverserez le net au vendeur.
          Webhooks à déclarer : <code className="text-sky-300">/api/webhooks/wave</code> et <code className="text-sky-300">/api/webhooks/orange-money</code>.
          Les clés <strong>Orange SMS API</strong> (developer.orange.com → SMS Sénégal) activent les rappels SMS aux acheteuses en retard de paiement.</p>
        <div className="grid sm:grid-cols-2 gap-2 mt-4">
          {FIELDS.map(([k, label]) => (
            <div key={k}>
              <label className="text-[11px] uppercase tracking-widest text-slate-500 flex items-center gap-1">{label}
                {cfg.configured[k] && <CheckCircle2 className="h-3 w-3 text-emerald-400" />}</label>
              <input data-testid={`pay-${k}`} type="password" autoComplete="off" placeholder={cfg[k] || 'Non configuré'} value={form[k] || ''}
                onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                className="w-full px-3 py-2 rounded-xl bg-slate-900 border border-slate-700 text-white outline-none text-sm" />
            </div>
          ))}
          <div>
            <label className="text-[11px] uppercase tracking-widest text-slate-500">Orange Money — environnement</label>
            <select data-testid="pay-om_env" value={form.om_env || 'sn'} onChange={(e) => setForm({ ...form, om_env: e.target.value })}
              className="w-full px-3 py-2 rounded-xl bg-slate-900 border border-slate-700 text-white outline-none text-sm">
              <option value="sn">Production Sénégal (sn)</option><option value="dev">Sandbox (dev)</option>
            </select>
          </div>
        </div>
        <div className="mt-3 flex items-center justify-between gap-3 text-xs text-slate-400">
          <span>Laissez vide pour conserver la valeur actuelle. Statut : Wave {cfg.configured.wave_api_key && cfg.configured.wave_webhook_secret ? '✅ actif' : '⏳ en attente des clés'} · Orange Money {cfg.configured.om_client_id && cfg.configured.om_merchant_key ? '✅ actif' : '⏳ en attente des clés'} · SMS <span data-testid="sms-status">{cfg.configured.sms_client_id && cfg.configured.sms_client_secret && cfg.configured.sms_sender ? '✅ actif' : '⏳ en attente des clés'}</span></span>
          <Button size="sm" data-testid="pay-settings-save" onClick={save} className="btn-brand text-white">Enregistrer</Button>
        </div>
      </section>

      {payouts && (
        <section className="card-elev rounded-2xl p-5" data-testid="payouts-panel">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="font-display text-xl font-bold flex items-center gap-2"><Banknote className="h-5 w-5 text-emerald-400" />Reversements aux vendeurs</h2>
            <div className="text-sm"><span className="text-slate-400">À reverser : </span><span className="font-mono-num text-amber-400 font-bold" data-testid="payouts-pending">{fcfa(payouts.pending)}</span>
              <span className="text-slate-400 ml-3">Commissions encaissées : </span><span className="font-mono-num text-emerald-400 font-bold" data-testid="payouts-collected">{fcfa(payouts.collected)}</span></div>
          </div>
          <div className="mt-3 space-y-2">
            {payouts.orders.length === 0 && <div className="text-slate-500 text-sm">Aucun paiement via la plateforme pour l'instant.</div>}
            {payouts.orders.map((o) => (
              <div key={o.order_id} data-testid={`payout-${o.order_id}`} className="p-3 rounded-xl bg-slate-900/60 border border-slate-800 text-sm flex flex-wrap items-center justify-between gap-2">
                <div>
                  <div className="font-bold">{o.product_name} <span className="text-slate-500 font-normal">· {o.seller_name}</span></div>
                  <div className="text-xs text-slate-400">{fcfa(o.amount)} payé via {o.payment_method === 'wave' ? 'Wave' : 'Orange Money'} → net vendeur <span className="text-emerald-400 font-mono-num">{fcfa(o.seller_net)}</span>
                    {' '}· {o.seller_wave_number || o.seller_orange_number || 'numéro vendeur manquant'}</div>
                </div>
                {o.payout_status === 'paid' ? <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300">Reversé</span>
                  : <Button size="sm" data-testid={`payout-paid-${o.order_id}`} onClick={() => markPaid(o.order_id)} className="bg-emerald-600 text-white">Marquer reversé</Button>}
              </div>
            ))}
          </div>
        </section>
      )}
    </>
  );
};
